import { useState } from "react"
import { X, BookOpen, FileText, Video, Image as ImageIcon, Download, HelpCircle, Clock, ChevronDown, ChevronRight } from "lucide-react"
import { Button } from "../ui/button"

export default function CoursePreviewModal({
  course,
  modules = [],
  onClose
}) {
  const [expandedModules, setExpandedModules] = useState(new Set())
  const [expandedLessons, setExpandedLessons] = useState(new Set()) // NEW: Expanded lessons

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev)
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId)
      } else {
        newSet.add(moduleId)
      }
      return newSet
    })
  }

  // NEW: Toggle lesson expansion
  const toggleLesson = (lessonId) => {
    setExpandedLessons(prev => {
      const newSet = new Set(prev)
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId)
      } else {
        newSet.add(lessonId)
      }
      return newSet
    })
  }

  const getBlockIcon = (type) => {
    switch (type) {
      case 'VIDEO':
        return <Video className="w-4 h-4 text-[#203a8a]" />
      case 'TEXT':
        return <FileText className="w-4 h-4 text-[#203a8a]" />
      case 'IMAGE':
        return <ImageIcon className="w-4 h-4 text-[#203a8a]" />
      case 'DOWNLOAD':
        return <Download className="w-4 h-4 text-[#203a8a]" />
      case 'ASSIGNMENT':
        return <FileText className="w-4 h-4 text-[#203a8a]" />
      default:
        return <FileText className="w-4 h-4 text-gray-500" />
    }
  }

  const getBlockTypeLabel = (type) => {
    switch (type) {
      case 'VIDEO':
        return 'Vidéo'
      case 'TEXT':
        return 'Texte'
      case 'IMAGE':
        return 'Image'
      case 'DOWNLOAD':
        return 'Téléchargement'
      case 'ASSIGNMENT':
        return 'Devoir'
      default:
        return type
    }
  }

  // Trier les modules par ordre
  const sortedModules = [...modules].sort((a, b) => (a.order || 0) - (b.order || 0))

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-[#141835] to-[#203a8a] text-white relative">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6" />
            <div>
              <h2 className="text-2xl font-bold">Prévisualisation du cours</h2>
              <p className="text-sm text-gray-200 mt-1">{course?.title || 'Sans titre'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {course?.thumbnail && (
              <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-white/30 shadow-lg">
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-10 w-10 p-0 text-white hover:bg-white/20 border border-white/30 rounded-full"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Course Info */}
          {course && (
            <div className="mb-6 bg-gray-50 rounded-lg p-6 border border-gray-200">
              <div className="flex items-start gap-4">
                {course.thumbnail && (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-32 h-24 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h3>
                  {course.shortDescription && (
                    <p className="text-gray-600 mb-2">{course.shortDescription}</p>
                  )}
                  {course.description && (
                    <p className="text-gray-700 text-sm">{course.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
                    {course.duration > 0 && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{Math.floor(course.duration / 60)}h {course.duration % 60}min</span>
                      </div>
                    )}
                    {course.level && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {course.level === 'BEGINNER' ? 'Débutant' : course.level === 'INTERMEDIATE' ? 'Intermédiaire' : 'Avancé'}
                      </span>
                    )}
                    {course.language && (
                      <span className="text-gray-600">{course.language}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modules */}
          {sortedModules.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg">Aucun module dans ce cours</p>
              <p className="text-sm mt-2">Ajoutez des modules pour commencer à construire votre cours</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedModules.map((module, moduleIndex) => {
                const isExpanded = expandedModules.has(module.id)
                // NEW: Récupérer les chapitres du module (les blocs sont maintenant dans les chapitres)
                const sortedLessons = [...(module.lessons || [])].sort((a, b) => (a.order || 0) - (b.order || 0))
                // Quiz peut être un objet avec une propriété quiz ou un tableau
                const quizData = module.quiz
                const hasQuiz = quizData && (
                  (Array.isArray(quizData) && quizData.length > 0) ||
                  (typeof quizData === 'object' && quizData.quiz)
                )

                return (
                  <div key={module.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Module Header */}
                    <div
                      className="bg-white p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleModule(module.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button className="p-1 hover:bg-gray-200 rounded">
                            {isExpanded ? (
                              <ChevronDown className="w-5 h-5 text-gray-600" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-[#141835]" />
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                Module {moduleIndex + 1}: {module.title}
                              </h3>
                              {module.description && (
                                <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{sortedLessons.length} chapitre{sortedLessons.length !== 1 ? 's' : ''}</span>
                          {hasQuiz && (
                            <span className="flex items-center gap-1">
                              <HelpCircle className="w-4 h-4" />
                              Quiz
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Module Content */}
                    {isExpanded && (
                      <div className="bg-gray-50 border-t border-gray-200">
                        {/* NEW: Lessons */}
                        {sortedLessons.length > 0 ? (
                          <div className="p-4 space-y-3">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">chapitres du module</h4>
                            {sortedLessons.map((lesson, lessonIndex) => {
                              const isLessonExpanded = expandedLessons.has(lesson.id)
                              // Trier les blocs de la chapitre par ordre
                              const sortedBlocks = [...(lesson.blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0))
                              
                              return (
                                <div key={lesson.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                  {/* Lesson Header */}
                                  <div
                                    className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleLesson(lesson.id)}
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <button className="p-1 hover:bg-gray-200 rounded">
                                          {isLessonExpanded ? (
                                            <ChevronDown className="w-4 h-4 text-gray-600" />
                                          ) : (
                                            <ChevronRight className="w-4 h-4 text-gray-600" />
                                          )}
                                        </button>
                                        <div>
                                          <h5 className="font-medium text-gray-900 text-sm">
                                            chapitre {lessonIndex + 1}: {lesson.title}
                                          </h5>
                                          {lesson.description && (
                                            <p className="text-xs text-gray-600 mt-1">{lesson.description}</p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3 text-xs text-gray-500">
                                        {lesson.duration > 0 && (
                                          <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {lesson.duration} min
                                          </span>
                                        )}
                                        <span>{sortedBlocks.length} bloc{sortedBlocks.length !== 1 ? 's' : ''}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Lesson Content - Blocks */}
                                  {isLessonExpanded && sortedBlocks.length > 0 && (
                                    <div className="bg-gray-50 border-t border-gray-200 p-3 space-y-2">
                                      {sortedBlocks.map((block, blockIndex) => (
                                        <div
                                          key={block.id}
                                          className="bg-white rounded-lg p-3 border border-gray-200 hover:border-[#141835] transition-colors"
                                        >
                                          <div className="flex items-start gap-3">
                                            <div className="mt-1">
                                              {getBlockIcon(block.type)}
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xs font-medium text-gray-500">
                                                  Bloc {blockIndex + 1}
                                                </span>
                                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                                  {getBlockTypeLabel(block.type)}
                                                </span>
                                              </div>
                                              <h6 className="font-medium text-gray-900 text-sm mb-1">{block.title}</h6>
                                              {block.description && (
                                                <p className="text-xs text-gray-600 mb-2">{block.description}</p>
                                              )}
                                              {block.content && (
                                                <div className="text-xs text-gray-500 mt-2">
                                                  {block.type === 'TEXT' && block.content.html && (
                                                    <div className="line-clamp-2" dangerouslySetInnerHTML={{ __html: block.content.html.substring(0, 200) + '...' }} />
                                                  )}
                                                  {block.type === 'VIDEO' && block.content.videoUrl && (
                                                    <span>Vidéo: {block.content.videoUrl.substring(0, 50)}...</span>
                                                  )}
                                                  {block.type === 'IMAGE' && block.content.imageUrl && (
                                                    <span>Image: {block.content.imageUrl.substring(0, 50)}...</span>
                                                  )}
                                                  {block.duration > 0 && (
                                                    <span className="ml-2">• {block.duration} min</span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {isLessonExpanded && sortedBlocks.length === 0 && (
                                    <div className="bg-gray-50 border-t border-gray-200 p-3 text-center text-xs text-gray-500">
                                      Aucun bloc dans cette chapitre
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Aucune chapitre dans ce module
                          </div>
                        )}

                        {/* Quiz */}
                        {hasQuiz && (
                          <div className="p-4 border-t border-gray-200">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                              <HelpCircle className="w-4 h-4" />
                              Quiz du module
                            </h4>
                            {(() => {
                              // Gérer différents formats de quiz
                              let quizzes = []
                              if (Array.isArray(quizData)) {
                                quizzes = quizData
                              } else if (quizData.quiz) {
                                quizzes = [quizData.quiz]
                              } else if (quizData.id) {
                                quizzes = [quizData]
                              }

                              return quizzes.map((quizItem) => {
                                const quiz = quizItem.quiz || quizItem
                                return (
                                  <div
                                    key={quiz.id || quizItem.id}
                                    className="bg-white rounded-lg p-4 border border-gray-200"
                                  >
                                    <div className="flex items-start gap-3">
                                      <HelpCircle className="w-5 h-5 text-[#203a8a] mt-1" />
                                      <div className="flex-1">
                                        <h5 className="font-medium text-gray-900 mb-1">{quiz.title}</h5>
                                        {quiz.description && (
                                          <p className="text-sm text-gray-600 mb-2">{quiz.description}</p>
                                        )}
                                        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                                          {quiz.timeLimit && (
                                            <span className="flex items-center gap-1">
                                              <Clock className="w-3 h-3" />
                                              {quiz.timeLimit} min
                                            </span>
                                          )}
                                          {quiz.passingScore && (
                                            <span>Score de passage: {quiz.passingScore}%</span>
                                          )}
                                          {quiz.maxAttempts && (
                                            <span>Tentatives max: {quiz.maxAttempts}</span>
                                          )}
                                          {quiz.questions && quiz.questions.length > 0 && (
                                            <span>{quiz.questions.length} question{quiz.questions.length > 1 ? 's' : ''}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })
                            })()}
                          </div>
                        )}

                        {sortedLessons.length === 0 && !hasQuiz && (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            Aucun contenu dans ce module
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <Button
            onClick={onClose}
            className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  )
}

